import { useEffect, useRef, useState } from 'react';

export interface SavedResult {
  id: number;
  title: string;
  source: string;
  date: string;
  type: string;
  snippet: string;
  url: string;
  savedAt: number;
}

interface Props {
  open: boolean;
  items: SavedResult[];
  onClose: () => void;
  onRemove: (id: number) => void;
  onCite: (item: SavedResult) => void;
  onOpen: (item: SavedResult) => void;
}

const TYPE_COLORS: Record<string, string> = {
  'Academic Paper': 'bg-violet-100 text-violet-700',
  'Research Study': 'bg-cyan-100 text-cyan-700',
  'News Article': 'bg-emerald-100 text-emerald-700',
  Book: 'bg-amber-100 text-amber-700',
  Video: 'bg-rose-100 text-rose-700',
};

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

/* ── export helpers ── */
function escapeCSV(val: string) {
  const str = String(val).replace(/"/g, '""');
  return /[",\n\r]/.test(str) ? `"${str}"` : str;
}

function exportTXT(items: SavedResult[]) {
  const lines: string[] = [
    'OpenClaw AI Search — Saved Library Export',
    `Exported: ${new Date().toLocaleString()}`,
    `Total items: ${items.length}`,
    '═'.repeat(60),
    '',
  ];
  items.forEach((item, i) => {
    lines.push(`[${i + 1}] ${item.title}`);
    lines.push(`    Type:    ${item.type}`);
    lines.push(`    Source:  ${item.source}  (${item.date})`);
    lines.push(`    URL:     ${item.url}`);
    lines.push(`    Snippet: ${item.snippet}`);
    lines.push(`    Saved:   ${new Date(item.savedAt).toLocaleString()}`);
    lines.push('─'.repeat(60));
    lines.push('');
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `openclaw-library-${Date.now()}.txt`;
  a.click();
}

function exportCSV(items: SavedResult[]) {
  const headers = ['Title', 'Type', 'Source', 'Date', 'URL', 'Snippet', 'Saved At'];
  const rows = items.map(item => [
    escapeCSV(item.title),
    escapeCSV(item.type),
    escapeCSV(item.source),
    escapeCSV(item.date),
    escapeCSV(item.url),
    escapeCSV(item.snippet),
    escapeCSV(new Date(item.savedAt).toLocaleString()),
  ].join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `openclaw-library-${Date.now()}.csv`;
  a.click();
}

/* ── citation formatters ── */
type CitationStyle = 'APA' | 'MLA' | 'Chicago';

const CITE_FORMATS: Record<CitationStyle, (r: SavedResult) => string> = {
  APA: (r) => `${r.source}. (${r.date}). ${r.title}. Retrieved from ${r.url}`,
  MLA: (r) => `"${r.title}." ${r.source}, ${r.date}, ${r.url}.`,
  Chicago: (r) => `${r.source}. "${r.title}." ${r.date}. ${r.url}.`,
};

function buildReferenceList(items: SavedResult[], style: CitationStyle): string {
  const header = `OpenClaw AI Search — Reference List (${style})\nGenerated: ${new Date().toLocaleString()}\n${'─'.repeat(60)}\n\n`;
  const entries = items.map((item, i) => `[${i + 1}] ${CITE_FORMATS[style](item)}`).join('\n\n');
  return header + entries;
}

export default function SavedLibraryDrawer({ open, items, onClose, onRemove, onCite, onOpen }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportBtnRef = useRef<HTMLDivElement>(null);

  /* ── bulk citations state ── */
  const [bulkCiteStyle, setBulkCiteStyle] = useState<CitationStyle>('APA');
  const [bulkCopied, setBulkCopied] = useState(false);
  const [showCitationPreview, setShowCitationPreview] = useState(false);

  const copyAllCitations = () => {
    const text = buildReferenceList(items, bulkCiteStyle);
    navigator.clipboard.writeText(text);
    setBulkCopied(true);
    setTimeout(() => setBulkCopied(false), 2200);
  };

  /* close export menu on outside click */
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (exportBtnRef.current && !exportBtnRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && open) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  /* trap scroll */
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white z-50 flex flex-col transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <i className="ri-bookmark-fill text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Saved Library</h2>
              <p className="text-xs text-slate-500">
                {items.length === 0 ? 'No saved results' : `${items.length} saved result${items.length > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-slate-500 text-xl"></i>
          </button>
        </div>

        {/* ── Copy All Citations Panel ── */}
        {items.length > 0 && (
          <div className="border-b border-slate-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-4 py-3">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-violet-600 flex items-center justify-center">
                  <i className="ri-double-quotes-r text-white text-xs"></i>
                </div>
                <span className="text-xs font-bold text-slate-800">Copy All Citations</span>
                <span className="text-[10px] text-slate-400">({items.length} sources)</span>
              </div>
              <button
                onClick={() => setShowCitationPreview(v => !v)}
                className="text-[10px] text-violet-600 hover:text-violet-800 font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className={`ri-eye-${showCitationPreview ? 'off' : 'line'} text-xs`}></i>
                {showCitationPreview ? 'Hide' : 'Preview'}
              </button>
            </div>

            {/* Style tabs */}
            <div className="flex gap-1 bg-white/70 rounded-lg p-1 mb-2.5">
              {(['APA', 'MLA', 'Chicago'] as CitationStyle[]).map((s) => (
                <button
                  key={s}
                  onClick={() => { setBulkCiteStyle(s); setBulkCopied(false); }}
                  className={`flex-1 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    bulkCiteStyle === s
                      ? 'bg-violet-600 text-white'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Preview box */}
            {showCitationPreview && (
              <div className="mb-2.5 bg-white border border-violet-200 rounded-lg p-3 max-h-28 overflow-y-auto">
                {items.slice(0, 3).map((item, i) => (
                  <p key={item.id} className="text-[10px] text-slate-600 leading-relaxed mb-1 last:mb-0">
                    <span className="text-violet-500 font-bold">[{i + 1}]</span> {CITE_FORMATS[bulkCiteStyle](item)}
                  </p>
                ))}
                {items.length > 3 && (
                  <p className="text-[10px] text-slate-400 italic mt-1">
                    +{items.length - 3} more citation{items.length - 3 > 1 ? 's' : ''}…
                  </p>
                )}
              </div>
            )}

            {/* Copy button */}
            <button
              onClick={copyAllCitations}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                bulkCopied
                  ? 'bg-emerald-500 text-white'
                  : 'bg-violet-600 hover:bg-violet-700 text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-xs ${bulkCopied ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
              </div>
              {bulkCopied ? `Copied ${items.length} citations!` : `Copy All ${bulkCiteStyle} Citations`}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
                <i className="ri-bookmark-line text-3xl text-amber-400"></i>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">No saved results yet</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Click the <strong className="text-amber-600">Save</strong> button on any search result to add it here. Your library persists across sessions.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:border-amber-300 transition-all group"
                >
                  {/* Type badge + time */}
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${TYPE_COLORS[item.type] ?? 'bg-slate-100 text-slate-600'}`}>
                      {item.type}
                    </span>
                    <span className="text-[11px] text-slate-400 flex items-center gap-1 shrink-0">
                      <i className="ri-time-line text-xs"></i>
                      {timeAgo(item.savedAt)}
                    </span>
                  </div>

                  <h4 className="text-sm font-bold text-slate-900 mb-1 leading-snug line-clamp-2 group-hover:text-amber-700 transition-colors">
                    {item.title}
                  </h4>

                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-2">
                    <i className="ri-book-line"></i>
                    <span>{item.source}</span>
                    <span>·</span>
                    <span>{item.date}</span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-3">
                    {item.snippet}
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onCite(item)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-xs font-semibold hover:bg-violet-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-double-quotes-r text-xs"></i>
                      Cite
                    </button>
                    <button
                      onClick={() => onOpen(item)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      Open
                    </button>
                    <button
                      onClick={() => onRemove(item.id)}
                      className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-4 bg-slate-50 space-y-3">
            {/* Export Library button */}
            <div ref={exportBtnRef} className="relative">
              <button
                onClick={() => setShowExportMenu(v => !v)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-300 hover:border-violet-400 hover:bg-violet-50 rounded-xl text-sm font-semibold text-slate-700 hover:text-violet-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-download-2-line text-base"></i>
                </div>
                Export Library
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-${showExportMenu ? 'up' : 'down'}-s-line text-sm`}></i>
                </div>
              </button>

              {/* Export dropdown */}
              {showExportMenu && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg z-10">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-[11px] text-slate-400 font-medium">
                      Export {items.length} saved result{items.length > 1 ? 's' : ''} as…
                    </p>
                  </div>
                  <button
                    onClick={() => { exportTXT(items); setShowExportMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-violet-100 flex items-center justify-center transition-colors">
                      <i className="ri-file-text-line text-slate-600 group-hover:text-violet-600 text-base transition-colors"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Plain Text (.txt)</p>
                      <p className="text-[11px] text-slate-500">Formatted readable document with all details</p>
                    </div>
                    <div className="ml-auto w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-slate-400 group-hover:text-violet-500 transition-colors"></i>
                    </div>
                  </button>
                  <button
                    onClick={() => { exportCSV(items); setShowExportMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer text-left group border-t border-slate-100"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                      <i className="ri-file-excel-line text-slate-600 group-hover:text-emerald-600 text-base transition-colors"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Spreadsheet (.csv)</p>
                      <p className="text-[11px] text-slate-500">Import into Excel, Sheets, or any spreadsheet app</p>
                    </div>
                    <div className="ml-auto w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-slate-400 group-hover:text-emerald-500 transition-colors"></i>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
              <i className="ri-lock-line"></i>
              Saved locally on your device · persists across sessions
            </p>
          </div>
        )}
      </div>
    </>
  );
}
